import React, { useEffect, useRef, useState } from 'react';
import V4Tabs from './V4Tabs';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';

const DocumentsUploadV4 = () => {
  const { getAuthHeaders } = useAuth();
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [progress, setProgress] = useState(null);
  const [batchJobs, setBatchJobs] = useState([]); // [{ jobId, fileName }]
  const [batchStatuses, setBatchStatuses] = useState({}); // jobId -> status/progress
  const [expandedJobs, setExpandedJobs] = useState({}); // jobId -> bool
  const [uploading, setUploading] = useState(false);
  const poller = useRef(null);

  const startPolling = (jobId) => {
    if (poller.current) clearInterval(poller.current);
    poller.current = setInterval(async () => {
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/jobs/${jobId}`, { headers: { ...getAuthHeaders() } });
      const data = await res.json();
      setProgress(data);
      if (data.status === 'completed' || data.status === 'failed') {
        clearInterval(poller.current);
        setUploading(false);
      }
    }, 1500);
  };

  const startBatchPolling = (jobs) => {
    if (poller.current) clearInterval(poller.current);
    poller.current = setInterval(async () => {
      const updates = {};
      for (const item of jobs) {
        const j = typeof item === 'string' ? item : item.jobId;
        try {
          const res = await fetch(`${API_BASE_URL}/api/v4/documents/jobs/${j}`, { headers: { ...getAuthHeaders() } });
          updates[j] = await res.json();
        } catch (_) {
          updates[j] = { job_id: j, status: 'error', progress: 0 };
        }
      }
      setBatchStatuses(updates);
      const allDone = Object.values(updates).every((s) => s.status === 'completed' || s.status === 'failed');
      if (allDone) {
        clearInterval(poller.current);
        setUploading(false);
      }
    }, 1500);
  };

  const cancelJob = async (jobId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/jobs/${jobId}/cancel`, {
        method: 'POST',
        headers: { ...getAuthHeaders() },
      });
      if (res.ok) {
        // Optimistically mark as cancelled
        setBatchStatuses((prev) => ({ ...prev, [jobId]: { ...(prev[jobId]||{}), status: 'cancelled' } }));
        if (job?.job_id === jobId) setProgress({ ...(progress||{}), status: 'cancelled' });
      }
    } catch (e) {
      // no-op
    }
  };

  const toggleExpanded = (jobId) => {
    setExpandedJobs((prev) => ({ ...prev, [jobId]: !prev[jobId] }));
  };

  useEffect(() => () => { if (poller.current) clearInterval(poller.current); }, []);

  const submitUpload = async () => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    form.append('is_zip', String(file.name.toLowerCase().endsWith('.zip')));
    const authHeaders = getAuthHeaders();
    if ('Content-Type' in authHeaders) delete authHeaders['Content-Type'];
    setUploading(true);
    const res = await fetch(`${API_BASE_URL}/api/v4/documents/upload`, { method: 'POST', headers: { ...authHeaders }, body: form });
    if (!res.ok) { setUploading(false); return; }
    const data = await res.json();
    // Duplicate handling
    if (data && data.duplicate && data.existing) {
      const ex = data.existing;
      const ok = window.confirm(`A document named "${ex.name}" already exists.\n\nStatus: ${ex.status}\nProgress: ${ex.progress}%\nLast Updated: ${ex.updated_at || ''}\n\nDo you want to overwrite it with the new upload?`);
      if (!ok) { setUploading(false); return; }
      const form2 = new FormData();
      form2.append('file', file);
      form2.append('is_zip', String(file.name.toLowerCase().endsWith('.zip')));
      form2.append('overwrite', 'true');
      const res2 = await fetch(`${API_BASE_URL}/api/v4/documents/upload`, { method: 'POST', headers: { ...authHeaders }, body: form2 });
      if (!res2.ok) { setUploading(false); return; }
      const data2 = await res2.json();
      if (Array.isArray(data2.jobs) && data2.jobs.length > 0) {
        const jobs = data2.jobs.map((j) => ({ jobId: j.job_id, fileName: j.file }));
        setBatchJobs(jobs);
        startBatchPolling(jobs);
      } else {
        setJob(data2);
        if (data2.job_id) startPolling(data2.job_id);
      }
      return;
    }
    // ZIP returns multiple jobs
    if (Array.isArray(data.jobs) && data.jobs.length > 0) {
      const jobs = data.jobs.map((j) => ({ jobId: j.job_id, fileName: j.file }));
      setBatchJobs(jobs);
      startBatchPolling(jobs);
    } else {
      setJob(data);
      if (data.job_id) startPolling(data.job_id);
    }
  };

  return (
    <div className="container py-4">
      <h3 className="mb-3">Documents v4</h3>
      <V4Tabs />
      <div className="card">
        <div className="card-body">
          <div className="mb-2">Upload a document or a .zip for batch processing:</div>
          <label className="d-block border border-2 border-dashed rounded p-4 text-center bg-light" style={{ cursor: 'pointer' }}>
            <input type="file" className="d-none" onChange={(e) => setFile(e.target.files[0])} />
            <div className="mb-2"><span className="badge bg-primary-subtle text-primary">Choose file</span></div>
            <div className="text-muted small">{file ? file.name : 'Click to select a file (PDF, DOCX, XLSX, ZIP, etc.)'}</div>
          </label>
          <div className="mt-3 d-flex gap-2">
            <button className="btn btn-primary" onClick={submitUpload} disabled={!file || uploading}>Start Processing</button>
            <button className="btn btn-outline-secondary" onClick={() => setFile(null)}>Clear</button>
          </div>
          {job && (
            <div className="mt-3 small text-muted">Job: {job.job_id} (batch {job.batch_id}) - {job.status}</div>
          )}
          {batchJobs.length > 0 && (
            <div className="mt-3">
              <div className="fw-semibold small mb-2">Batch Jobs</div>
              <ul className="list-group">
                {batchJobs.map((item) => {
                  const jid = item.jobId || item;
                  const fname = item.fileName || '';
                  const st = batchStatuses[jid] || { status: 'queued', progress: 0 };
                  return (
                    <li key={jid} className="list-group-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="small">
                          <div className="fw-semibold">{fname || jid}</div>
                          <div className="text-muted">{jid}</div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span className={`badge ${st.status==='completed'?'bg-success':st.status==='failed'?'bg-danger':st.status==='cancelled'?'bg-secondary':'bg-warning text-dark'}`}>{st.status}</span>
                          <div className="progress" style={{ width: 120 }}>
                            <div className="progress-bar" style={{ width: `${Number(st.progress || 0)}%` }} />
                          </div>
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleExpanded(jid)}>{expandedJobs[jid] ? 'Hide' : 'Details'}</button>
                          {st.status !== 'completed' && st.status !== 'failed' && st.status !== 'cancelled' && (
                            <button className="btn btn-sm btn-outline-danger" onClick={() => cancelJob(jid)}>Cancel</button>
                          )}
                        </div>
                      </div>
                      {expandedJobs[jid] && (
                        <div className="mt-2">
                          {Array.isArray(st?.metadata?.pipeline) ? (
                            <ul className="list-group">
                              {st.metadata.pipeline.map((step, i) => (
                                <li key={i} className="list-group-item d-flex justify-content-between align-items-start">
                                  <div>
                                    <div className="fw-semibold">{step.name}</div>
                                    <div className="small text-muted">{step.message}</div>
                                  </div>
                                  <span className={`badge ${step.status==='completed'?'bg-success':step.status==='failed'?'bg-danger':'bg-warning text-dark'}`}>{step.status}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className="small text-muted">No step details yet.</div>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {progress && (
            <>
              <div className="mt-2 small">Status: {progress.status} | Progress: {progress.progress}%</div>
              <div className="progress mt-1" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={Number(progress.progress || 0)}>
                <div className="progress-bar" style={{ width: `${Number(progress.progress || 0)}%` }}>{Number(progress.progress || 0)}%</div>
              </div>
              {progress.metadata?.pipeline && Array.isArray(progress.metadata.pipeline) && (
                <div className="mt-3">
                  <div className="fw-semibold small mb-2">Pipeline</div>
                  <ul className="list-group">
                    {progress.metadata.pipeline.map((step, i) => (
                      <li key={i} className="list-group-item d-flex justify-content-between align-items-start">
                        <div>
                          <div className="fw-semibold">{step.name}</div>
                          <div className="small text-muted">{step.message}</div>
                        </div>
                        <span className={`badge ${step.status === 'completed' ? 'bg-success' : step.status === 'failed' ? 'bg-danger' : 'bg-warning text-dark'}`}>{step.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentsUploadV4;


